import { useEffect, useRef, useState } from 'react';
import { type AsyncRateLimiterState, useAsyncRateLimiter } from '@tanstack/react-pacer';
import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import { milliseconds } from 'date-fns';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

import { type VerifyV3StartResponse, verifyV3Start } from '@/api/generated';
import { logger } from '@/lib/logger';

// Rate limit: 2 attempts per 30 seconds
const RATE_LIMIT_WINDOW_MS = milliseconds({ seconds: 30 });
const RATE_LIMIT_ATTEMPTS = 2;

type MutationParams = {
  phoneNumber: string;
};

const fetcher = async (params: MutationParams) => {
  const { data } = await verifyV3Start({
    body: { phone_number: params.phoneNumber },
  });

  return data;
};

export function useSmsVerifyStartMutation(
  options?: UseMutationOptions<VerifyV3StartResponse, Error, MutationParams>
) {
  const intervalRef = useRef<number | null>(null);
  const [, forceUpdate] = useState({});

  const asyncRateLimiter = useAsyncRateLimiter(
    fetcher,
    {
      onError: (error) => {
        logger.error('Failed to send verification code', error);
      },
      limit: RATE_LIMIT_ATTEMPTS,
      window: RATE_LIMIT_WINDOW_MS,
      windowType: 'fixed',
      initialState: useSmsRateLimiterStore.getState().state ?? undefined,
    },
    // Selector to track state we need for UI updates
    (state) => state
  );

  useEffect(
    function persistState() {
      useSmsRateLimiterStore.getState().setState(asyncRateLimiter.state);
    },
    [asyncRateLimiter.state]
  );

  const remainingMs = asyncRateLimiter.getMsUntilNextWindow();

  useEffect(
    function resetExpiredRateLimiter() {
      if (asyncRateLimiter.state.isExceeded && remainingMs <= 0) {
        asyncRateLimiter.reset();
        useSmsRateLimiterStore.getState().resetState();
      }
    },
    [asyncRateLimiter, remainingMs]
  );

  const canResend = !asyncRateLimiter.state.isExceeded;

  useEffect(
    function forceCountdownRender() {
      if (!canResend) {
        intervalRef.current = window.setInterval(
          () => {
            // Force re-render to update countdown
            forceUpdate({});
          },
          milliseconds({ seconds: 1 })
        );
      } else {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      return () => {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
        }
      };
    },
    [canResend]
  );

  const mutation = useMutation({
    mutationFn: async (data) => {
      const result = await asyncRateLimiter.maybeExecute(data);

      if (result === undefined) {
        if (asyncRateLimiter.state.isExceeded) {
          throw new Error('Rate limit exceeded');
        }
        throw new Error('Failed to send verification code. Please try again.');
      }

      return result;
    },
    ...options,
  });

  return {
    ...mutation,
    remainingMs,
    canResend,
  };
}

type RateLimiterState = AsyncRateLimiterState<
  (data: MutationParams) => Promise<VerifyV3StartResponse>
>;

interface SmsRateLimiterStore {
  state: RateLimiterState | null;
  setState: (state: RateLimiterState) => void;
  resetState: () => void;
}

const useSmsRateLimiterStore = create<SmsRateLimiterStore>()(
  devtools(
    persist(
      (set) => ({
        state: null,
        setState: (state) => set({ state }),
        resetState: () => set({ state: null }),
      }),
      {
        name: 'sms-rate-limiter-storage',
        storage: createJSONStorage(() => sessionStorage),
      }
    ),
    {
      name: 'SmsRateLimiter',
      enabled: import.meta.env.MODE !== 'test',
    }
  )
);
